import { S3Client, GetObjectCommand, PutObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { fetchAuthSession } from 'aws-amplify/auth';

const BUCKET_NAME = 'asl-ceu-tracker-profile-photos';
const REGION = 'us-east-1';

// Initialize S3 client with Amplify v6 credentials
const getS3Client = async () => {
  try {
    const session = await fetchAuthSession();
    const credentials = session.credentials;

    return new S3Client({
      region: REGION,
      credentials: credentials
    });
  } catch (error) {
    console.error('Error creating S3 client:', error);
    throw error;
  }
};

export const securePhotoService = {
  /**
   * Generate pre-signed URL for viewing a profile photo (admin only)
   * @param {string} userId - User ID
   * @param {string} filename - Photo filename (e.g., 'profile.jpeg')
   * @returns {Promise<string>} Pre-signed URL
   */
  getViewUrl: async (userId, filename = 'profile.jpeg') => {
    try {
      const s3Client = await getS3Client();
      const key = `profile-photos/${userId}/${filename}`;

      const command = new GetObjectCommand({
        Bucket: BUCKET_NAME,
        Key: key,
      });

      // Generate pre-signed URL valid for 1 hour
      const signedUrl = await getSignedUrl(s3Client, command, { 
        expiresIn: 3600 // 1 hour
      });

      if (process.env.NODE_ENV === 'development') {
      }

      return signedUrl;
    } catch (error) {
      console.error('Error generating view URL:', error);
      return null;
    }
  },

  /**
   * Generate pre-signed URL for uploading a profile photo
   * @param {string} userId - User ID
   * @param {string} filename - Photo filename
   * @param {string} contentType - File content type
   * @returns {Promise<string>} Pre-signed upload URL
   */
  getUploadUrl: async (userId, filename, contentType) => {
    try {
      const s3Client = await getS3Client();
      const key = `profile-photos/${userId}/${filename}`;

      const command = new PutObjectCommand({
        Bucket: BUCKET_NAME,
        Key: key,
        ContentType: contentType,
        Metadata: {
          'uploaded-by': userId,
          'upload-timestamp': new Date().toISOString()
        }
      });

      // Generate pre-signed URL valid for 15 minutes
      const signedUrl = await getSignedUrl(s3Client, command, { 
        expiresIn: 900 // 15 minutes
      });

      if (process.env.NODE_ENV === 'development') {
      }

      return {
        uploadUrl: signedUrl,
        key: key,
        publicUrl: `https://${BUCKET_NAME}.s3.${REGION}.amazonaws.com/${key}`
      };
    } catch (error) {
      console.error('Error generating upload URL:', error);
      return null;
    }
  },

  /**
   * Generate pre-signed URL for certificate files
   * @param {string} certificateUrl - Full S3 URL from database
   * @returns {Promise<string>} Pre-signed URL
   */
  getCertificateViewUrl: async (certificateUrl) => {
    try {
      const s3Client = await getS3Client();
      
      // Extract the S3 key from the full URL
      // URL format: https://bucket.s3.region.amazonaws.com/key
      const url = new URL(certificateUrl);
      const key = url.pathname.substring(1); // Remove leading slash

      const command = new GetObjectCommand({
        Bucket: BUCKET_NAME,
        Key: key,
      });

      const signedUrl = await getSignedUrl(s3Client, command, { 
        expiresIn: 3600 // 1 hour
      });

      return signedUrl;
    } catch (error) {
      console.error('Error generating certificate view URL:', error);
      return null;
    }
  },

  /**
   * Batch generate view URLs for multiple users (admin only)
   * @param {Array} userIds - Array of user IDs
   * @returns {Promise<Object>} Object with userId -> signedUrl mapping
   */
  batchGetViewUrls: async (userIds) => {
    const urls = {};
    
    for (const userId of userIds) {
      try {
        const url = await securePhotoService.getViewUrl(userId);
        if (url) {
          urls[userId] = url;
        }
      } catch (error) {
        console.error(`Error getting URL for user ${userId}:`, error);
      }
    }

    return urls;
  }
};

export default securePhotoService;
